// PDF del preventivo: un solo costruttore per Nuovo, Modifica ed elenco
// Preventivi [sc-49299]. Prima ognuna delle tre pagine aveva il suo, uguale
// salvo la forma dei dati: ora ogni pagina prepara i dati (`datiPdf`) e qui
// si disegna, stampando solo i campi scelti nella finestra di stampa.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { indirizziPerPdf } from "./cliente";
import { TESTO_LINK_MAPPA } from "./indirizzo";
import { tabellaArticoli } from "./pdfArticoli";
import {
  INTESTAZIONE_AZIENDA,
  disegnaRiquadro,
  righeRiepilogo,
} from "./pdfRiepilogo";
import { metodoPagamentoPerPdf } from "./pagamenti";
import { righeSpese } from "./spese";
import { righeAcconti } from "./acconti";
import { CLAUSOLE_GIACENZA, stampa, tuttiICampi } from "./campiStampa";

const vuoto = (v) => v === null || v === undefined || String(v).trim() === "";

// location del preventivo: oggetto, oppure JSON in stringa
const asLoc = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

/**
 * Cliente nella forma del PDF, da un record con i campi del cliente: il
 * `Client` del preventivo (modifica, elenco) o il preventivo appena creato,
 * che li ha in piano.
 */
export function clientePerPdf(c = {}) {
  return {
    nome: c?.clientName,
    telefono: c?.phoneNumber,
    pec: c?.pec,
    email: c?.email,
    partitaIva: c?.vatNumber,
    codiceFiscale: c?.taxCode,
    azienda: c?.companyName,
    codiceUnivoco: c?.uniqueCode,
    indirizzi: indirizziPerPdf(c),
  };
}

/**
 * Dati del PDF da un preventivo. Ogni pagina passa il cliente, le righe della
 * tabella (il suo `buildQuoteTable`) e il totale articoli che ha in mano.
 */
export function datiPdf(quote, { cliente, righe, totaleArticoli }) {
  const loc = asLoc(quote?.location) || {};
  return {
    id: quote?.id,
    cliente,
    data: quote?.date,
    metodoPagamento: metodoPagamentoPerPdf(quote?.payment_method),
    consegna: { address: loc.address, link: loc.link },
    righe,
    importi: {
      totaleArticoli,
      maggiorazione: quote?.surcharge,
      maggiorazionePct: quote?.surcharge_percent,
      sconto: quote?.discount,
      scontoPct: quote?.discount_percent,
      acconto: quote?.deposit,
      spese: righeSpese(quote),
      acconti: righeAcconti(quote?.payments, quote?.deposit),
    },
    note: quote?.technical_queries,
  };
}

/** Nome del file: «Preventivo_N_12.pdf», «.dev» prima dell'estensione in sviluppo. */
export function nomeFilePreventivo(id) {
  const isDev =
    process.env.NODE_ENV === "development" ||
    process.env?.VUE_APP_NODE_ENV === "development";
  return isDev ? `Preventivo_N_${id}.dev.pdf` : `Preventivo_N_${id}.pdf`;
}

/**
 * Disegna il PDF del preventivo e restituisce il documento jsPDF.
 * `campi`: quelli scelti nella finestra di stampa; senza, tutti.
 */
export function pdfPreventivo(dati, campi = tuttiICampi(), { logo } = {}) {
  const si = (k) => stampa(campi, k);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const marginY = 10;
  let y = marginY;

  // — Logo + intestazione
  if (logo) {
    try {
      doc.addImage(logo, "PNG", marginX, y, 30, 30);
    } catch (err) {
      console.error("Logo non caricato", err);
    }
  }
  doc.setFontSize(9).setFont("helvetica", "normal");
  INTESTAZIONE_AZIENDA.forEach((text, i) => {
    const x = pageWidth - marginX - doc.getTextWidth(text);
    doc.text(text, x, y + 5 + i * 4);
  });
  y += 48;

  // «etichetta: valore»; senza valore «N/D»
  const printLabelValue = (label, value, xPos, yPos, maxWidth = null) => {
    doc.setFont("helvetica", "bolditalic");
    doc.text(`${label}:`, xPos, yPos);
    const off = doc.getTextWidth(`${label}: `) + 0.8;
    doc.setFont("helvetica", "normal");
    const val = value || "N/D";
    if (maxWidth) {
      const wrapped = doc.splitTextToSize(val, maxWidth - off);
      wrapped.forEach((ln, idx) => doc.text(ln, xPos + off, yPos + idx * 5));
      return yPos + wrapped.length * 5;
    }
    doc.text(val, xPos + off, yPos);
    return yPos + 5;
  };

  // — Dettaglio cliente (SX): il nome c'è sempre
  const c = dati.cliente || {};
  const leftX = marginX;
  doc
    .setFontSize(14)
    .setFont("helvetica", "bold")
    .text("Dettaglio cliente", leftX, y);
  let yLeft = y + 8;
  doc.setFontSize(11);
  yLeft = printLabelValue("cliente", c.nome, leftX, yLeft);
  if (si("telefono"))
    yLeft = printLabelValue("telefono", c.telefono, leftX, yLeft);
  if (si("contatto")) {
    yLeft = printLabelValue(
      c.pec ? "pec" : "email",
      c.pec || c.email || "",
      leftX,
      yLeft
    );
  }
  if (si("partitaIva"))
    yLeft = printLabelValue("partita iva", c.partitaIva, leftX, yLeft);
  if (si("codiceFiscale"))
    yLeft = printLabelValue("codice fiscale", c.codiceFiscale, leftX, yLeft);
  if (si("azienda"))
    yLeft = printLabelValue("azienda", c.azienda, leftX, yLeft);
  // prima solo il PDF del nuovo preventivo lo stampava («codice cliente»)
  if (si("codiceUnivoco") && !vuoto(c.codiceUnivoco)) {
    yLeft = printLabelValue("codice univoco", c.codiceUnivoco, leftX, yLeft);
  }
  // [sc-48860] sede legale (N/D se vuota), poi residenza e cantiere se compilati
  if (si("indirizziCliente")) {
    for (const v of c.indirizzi || []) {
      yLeft = printLabelValue(
        v.etichetta,
        v.address,
        leftX,
        yLeft,
        pageWidth / 2 - 20
      );
    }
  }

  // — Dettaglio preventivo (DX): sparisce se non resta nessun campo
  const rightX = pageWidth / 2 + 10;
  const colW = pageWidth - rightX - marginX;
  const metodo = si("metodoPagamento") ? dati.metodoPagamento : null;
  const link = si("mappa") ? dati.consegna?.link : null;
  let yRight = y;
  if (si("data") || metodo || si("indirizzoConsegna") || link) {
    doc
      .setFontSize(14)
      .setFont("helvetica", "bold")
      .text("Dettaglio preventivo", rightX, y);
    yRight = y + 8;
    doc.setFontSize(11);
    if (si("data")) {
      const data = new Date(dati.data).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      yRight = printLabelValue("data", data, rightX, yRight);
    }
    // [sc-49299] modalità di pagamento del preventivo, se già scelta
    if (metodo) {
      yRight = printLabelValue(
        "metodo di pagamento",
        metodo,
        rightX,
        yRight,
        colW
      );
    }
    if (si("indirizzoConsegna")) {
      yRight = printLabelValue(
        "indirizzo di consegna",
        dati.consegna?.address,
        rightX,
        yRight,
        colW
      );
    }
    if (link) {
      doc.setFont("helvetica", "bolditalic");
      doc.text("mappa:", rightX, yRight);
      const off = doc.getTextWidth("mappa: ") + 0.8;
      doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(0, 0, 255);
      // [sc-48860] testo leggibile al posto dell'URL troncato
      const disp = TESTO_LINK_MAPPA;
      doc.textWithLink(disp, rightX + off, yRight, { url: link });
      doc
        .setDrawColor(0, 0, 255)
        .line(
          rightX + off,
          yRight + 0.8,
          rightX + off + doc.getTextWidth(disp),
          yRight + 0.8
        )
        .setDrawColor(0);
      doc.setTextColor(0).setFontSize(11).setFont("helvetica", "normal");
      yRight += 5;
    }
  }

  y = Math.max(yLeft, yRight) + 4;
  doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
  y += 6;

  // — Tabella articoli
  const tbl = tabellaArticoli(dati.righe, { pageWidth, marginX, campi });
  autoTable(doc, {
    startY: y,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 1.5 },
    headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
    head: tbl.head,
    body: tbl.body,
    foot: tbl.foot,
    showFoot: "lastPage",
    columnStyles: tbl.columnStyles,
    tableWidth: tbl.tableWidth,
    margin: tbl.margin,
  });
  y = (doc.lastAutoTable?.finalY || y) + 10;

  // — Riepilogo come un conto [sc-48728]
  const imp = dati.importi || {};
  y = disegnaRiquadro(doc, {
    y,
    pageWidth,
    pageHeight,
    marginX,
    marginY,
    datiBancari: si("datiBancari"),
    firma: si("firma"),
    righe: righeRiepilogo({
      iva: tbl.ivaPerAliquota,
      totaleArticoli: imp.totaleArticoli,
      maggiorazione: imp.maggiorazione,
      maggiorazionePct: imp.maggiorazionePct,
      sconto: imp.sconto,
      scontoPct: imp.scontoPct,
      acconto: imp.acconto,
      spese: imp.spese,
      acconti: imp.acconti,
      mostra: {
        imponibileIva: si("imponibileIva"),
        spese: si("spese"),
        acconti: si("acconti"),
      },
    }),
  });

  // — Note
  if (si("note") && !vuoto(dati.note)) {
    if (pageHeight - y < 20) {
      doc.addPage();
      y = marginY;
    }
    doc.setFont("helvetica", "italic").setFontSize(8);
    const txt = doc.splitTextToSize(`${dati.note}`, pageWidth - 2 * marginX);
    doc.text(txt, marginX, y);
    y += txt.length * 3.5 + 4;
  }

  // — Clausole di giacenza, in fondo [sc-49299]
  if (si("clausole")) {
    doc.setFont("helvetica", "normal").setFontSize(8);
    const testo = doc.splitTextToSize(
      CLAUSOLE_GIACENZA.testo,
      pageWidth - 2 * marginX
    );
    if (pageHeight - y < 10 + testo.length * 3.5) {
      doc.addPage();
      y = marginY;
    }
    doc
      .setFont("helvetica", "bold")
      .setFontSize(9)
      .setTextColor(0)
      .text(CLAUSOLE_GIACENZA.titolo, marginX, y);
    y += 4.5;
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(80);
    doc.text(testo, marginX, y);
    doc.setTextColor(0);
  }

  return doc;
}
